import type { StreamingChatId, StreamingOperation } from "@storytell/streaming";
import { TbBrain, TbChevronRight } from "solid-icons/tb";
import { For, Show, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { BlockRenderer } from "../BlockRenderer";

interface ThinkingRendererProps {
  operation: StreamingOperation;
  chatId: StreamingChatId;
}

/**
 * Renders thinking operations with custom purple styling and collapsible content.
 * Shows "Thinking..." / "Thinking Process" with purple brain icon.
 * Expands to show all blocks within the thinking operation with smaller text.
 */
export const ThinkingRenderer = (props: ThinkingRendererProps) => {
  const wire = useWire();
  const stream = wire.stream;

  const [isExpanded, setIsExpanded] = createSignal(true); // Start expanded

  const title = () => {
    return props.operation.isComplete ? "Reasoning Process" : "Thinking...";
  };

  const isProcessing = () => !props.operation.isComplete;

  const blocks = () => {
    const allBlocks = [];
    for (const blockId of props.operation.blocks ?? []) {
      const block = stream.block.get(blockId);
      if (block) {
        allBlocks.push(block);
      }
    }
    return allBlocks;
  };

  const hasBlocks = () => blocks().length > 0;

  const hasNextOperation = () => {
    // Find the message containing this operation
    const messages = stream.chat.get(props.chatId)?.messages || [];
    for (const messageId of messages) {
      const message = stream.message.get(messageId);
      if (!message?.operations) continue;

      const operationIndex = message.operations.indexOf(props.operation.operationId);
      if (operationIndex !== -1) {
        // Check if there are more operations after this one
        return operationIndex < message.operations.length - 1;
      }
    }
    return false;
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded());
  };

  return (
    <div class="group relative" data-renderer="thinking-renderer">
      {/* Timeline connector - extends through entire operation height */}
      <Show when={hasNextOperation()}>
        <div
          class="absolute bottom-0 w-0.5 min-h-2 -ml-2 border-l-2 border-dotted border-gray-300 dark:border-slate-600"
          style={{
            left: "24px",
            top: "32px",
          }}
        />
      </Show>

      {/* Custom thinking header with purple brain icon */}
      <div class="flex items-start justify-between mb-4">
        <div class="flex items-start flex-1 gap-4 min-w-0">
          {/* Purple brain icon in circle */}
          <div class="flex-shrink-0 -mt-0.5">
            <div class="w-5 h-5 rounded-full relative top-[6px] left-[7px] bg-purple-600 dark:bg-purple-500 text-white flex items-center justify-center">
              <TbBrain class="w-3 h-3" />
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 group">
              <button
                type="button"
                onClick={handleToggle}
                class="text-base pt-0.5 font-medium text-primary-text transition-colors text-left"
              >
                {title()}
              </button>

              <Show when={hasBlocks()}>
                <button
                  type="button"
                  onClick={handleToggle}
                  class="flex-shrink-0 p-1 pt-2 hover:bg-canvas-hover rounded transition-colors"
                  title={isExpanded() ? "Collapse thinking" : "Expand thinking"}
                >
                  <TbChevronRight
                    class="size-3 stroke-3 text-muted-text transition-transform duration-100"
                    classList={{ "rotate-90": isExpanded() }}
                  />
                </button>
              </Show>
            </div>
          </div>
        </div>
      </div>

      {/* Content area with smaller text for thinking blocks */}
      <Show when={hasBlocks() && isExpanded()}>
        <div class="flex flex-col space-y-3 pb-3 min-w-0 pl-10">
          <div class="text-[15px] [&_p]:text-[15px] [&_div]:text-[15px] [&_span]:text-[15px] [&_h1]:text-base [&_h2]:text-base [&_h3]:text-[15px] [&_h4]:text-[15px] [&_li]:text-[15px]">
            <For each={blocks()}>{(block) => <BlockRenderer block={block} chatId={props.chatId} />}</For>
          </div>
        </div>
      </Show>
    </div>
  );
};
